import { SectionQuestions } from '@/pages/coach/coach-practitioner-journey/forms/dynamic-form';
import {
  Alert,
  Button,
  CheckboxGroup,
  Dialog,
  DialogPosition,
  Divider,
  IMAGE_WIDTH,
  ImageInput,
  Typography,
  renderIcon,
} from '@ecdlink/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { traineeSelectors } from '@/store/trainee';
import { coachSelectors } from '@/store/coach';
import { authSelectors } from '@/store/auth';
import { SmartSpaceVisitData } from '@/store/trainee/trainee.types';
import { PractitionerDto } from '@ecdlink/core';
import { DateFormats } from '@/constants/Dates';
import { classroomsSelectors } from '@/store/classroom';
import ROUTES from '@/routes/routes';
import SignatureCanvas from 'react-signature-canvas';
import { useHistory } from 'react-router';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/solid';
import { useAppDispatch } from '@/store';
import { imageExtensions } from '@/constants/AcceptableImageExtensions';
import { PhotoPrompt } from '@/components/photo-prompt/photo-prompt';
import { FileTypeEnum, FileUpload, WorkflowStatusEnum } from '@ecdlink/graphql';
import { documentThunkActions } from '@/store/document';

interface SmartSpaceCheck1Props {
  practitioner: PractitionerDto;
  coachSmartSpaceVisitId: string;
  onSubmit: () => void;
  setNotificationStep: (value: string) => void;
  saveSmartSpaceCheckData: (
    value: SmartSpaceVisitData[],
    visitSection: string
  ) => void;
  handleNextSection: () => void;
}

export const SmartSpaceCheck11: React.FC<SmartSpaceCheck1Props> = ({
  practitioner,
  coachSmartSpaceVisitId,
  onSubmit,
  saveSmartSpaceCheckData,
  setNotificationStep,
}) => {
  const appDispatch = useAppDispatch();
  const visitSection = 'Final Coach Agreement';
  const visitData = useSelector(
    traineeSelectors.getCoachSmartSpaceSectionAnswers(
      coachSmartSpaceVisitId,
      'Final Coach Agreement'
    )
  );
  const history = useHistory();
  const coach = useSelector(coachSelectors.getCoach);
  const user = useSelector(authSelectors.getAuthUser);
  const isCoach = coach?.user?.id === user?.id;

  const classroomGroups = useSelector(
    classroomsSelectors.getClassroomGroupsForUser(practitioner?.userId!)
  );
  const classroom = classroomGroups[0].classroom;

  const location = useMemo(() => {
    const address = [
      classroom?.siteAddress?.addressLine1,
      classroom?.siteAddress?.addressLine2,
      classroom?.siteAddress?.addressLine3,
      classroom?.siteAddress?.province?.description,
    ];

    if (address.every((item) => item === null || item === undefined)) {
      return 'No address available';
    }

    return `${address[0] || ''} ${address[1] || ''} ${address[2] || ''} ${
      address[3]
    }`;
  }, [classroom]);

  const formattedDate = useMemo(() => {
    return `${new Date().toLocaleString(
      'en-za',
      DateFormats.dayFullMonthYear
    )}`;
  }, []);

  const [siteImageOne, setSiteImageOne] = useState<string>('');

  const [siteImageTwo, setSiteImageTwo] = useState<string>('');

  const [isPhotoActionBarVisibleOne, setPhotoActionBarVisibleOne] =
    useState(false);
  const [isPhotoActionBarVisibleTwo, setPhotoActionBarVisibleTwo] =
    useState(false);

  let signature = {} as SignatureCanvas | null;

  const saveSignature = (signature: string) => {
    const copy = Object.assign({});
    if (copy.signingSignature !== signature) {
      copy.signingSignature = signature;

      if (copy.signingSignature != null) {
        // Perform a deep copy to avoid mutating the state directly
        setAnswers((prevQuestions) => {
          const updatedQuestions = [...prevQuestions]; // Shallow copy of the array
          updatedQuestions[2] = {
            ...updatedQuestions[2], // Spread the second item to maintain existing data
            questionAnswer: copy.signingSignature, // Update the specific field
          };
          return updatedQuestions; // Return the updated array
        });
      }
    }
  };

  const clearSignature = () => {
    signature?.clear();
    saveSignature('');
  };

  useEffect(() => {
    if (
      (siteImageOne === '' || siteImageOne === null) &&
      (siteImageTwo !== '' || siteImageTwo !== null)
    ) {
      setSiteImageOne(siteImageTwo);
      setSiteImageTwo('');
    }
  }, [siteImageOne, siteImageTwo]);

  const [questions, setAnswers] = useState<SmartSpaceVisitData[]>([
    {
      visitSection,
      question: 'First photo',
      questionAnswer: '',
    },
    {
      visitSection,
      question: `I, ${coach?.user?.firstName}, confirm that I have observed the programme of ${practitioner.user?.firstName} at ${location} on this day of ${formattedDate}.`,
      questionAnswer: 'false',
    },
    {
      visitSection,
      question: 'Practitioner Signature',
      questionAnswer: '',
    },
    {
      visitSection,
      question: 'Second photo',
      questionAnswer: '',
    },
  ]);

  const uploadImage = async (index: number) => {
    const result = await appDispatch(
      documentThunkActions.createFile({
        base64File: index === 0 ? siteImageOne : siteImageTwo,
        fileName: `SiteImage_${index === 0 ? '1' : '2'}.png`,
        fileType: FileTypeEnum.SiteAddressImage,
      })
    );
    const url = (result.payload as { url: string }).url;
    onOptionSelected(url, index);
  };

  useEffect(() => {
    if (!!visitData && visitData.length > 0) {
      const sortedVisitData = [...visitData].sort((a, b) => {
        if (a.question < b.question) return -1;
        if (a.question > b.question) return 1;
        return 0;
      });

      setAnswers(sortedVisitData);
    }
  }, [visitData]);

  useEffect(() => {
    if (siteImageOne !== '' && questions[0].questionAnswer === '') {
      uploadImage(0);
    }
    if (siteImageTwo !== '' && questions[3].questionAnswer === '') {
      uploadImage(3);
    }
  }, [siteImageOne, siteImageTwo]);

  const onOptionSelected = useCallback(
    (value: string, index: number) => {
      const updatedQuestions = questions.map((question, idx) =>
        idx === index
          ? {
              ...question,
              questionAnswer: value,
            }
          : question
      );
      setAnswers(updatedQuestions);
    },
    [questions, siteImageOne, siteImageTwo]
  );
  return (
    <>
      <div className="p-4">
        <Typography
          type="h2"
          text="Capture site evidence"
          color="textDark"
          className="my-3"
        />
        <Divider dividerType="dashed" className="my-4" />

        {!isCoach ? (
          <Alert
            className="my-4"
            type="warning"
            title="You are viewing this form and cannot fill in responses."
          />
        ) : null}

        {/* Question one */}

        <div key={'Question 1'}>
          <Typography
            type="body"
            text={questions[0].question}
            color="primary"
          />
          {isCoach ? (
            <>
              <ImageInput
                className="mb-4"
                acceptedFormats={imageExtensions}
                resolutionLimit={IMAGE_WIDTH}
                label=""
                nameProp="SitePhotoOne"
                icon="CameraIcon"
                iconContainerColor="tertiary"
                currentImageString={siteImageOne}
                overrideOnClick={() => setPhotoActionBarVisibleOne(true)}
              />

              {isPhotoActionBarVisibleOne && (
                <Dialog visible position={DialogPosition.Bottom} stretch>
                  <PhotoPrompt
                    title="Collage photo"
                    resolutionLimit={IMAGE_WIDTH}
                    onClose={() => setPhotoActionBarVisibleOne(false)}
                    onAction={(base64: string) => {
                      onOptionSelected('', 0);
                      setSiteImageOne(base64);
                      setPhotoActionBarVisibleOne(false);
                    }}
                    onDelete={() => {
                      onOptionSelected('', 0);
                      setSiteImageOne('');
                      setPhotoActionBarVisibleOne(false);
                    }}
                  />
                </Dialog>
              )}
            </>
          ) : (
            <img src={questions[0].questionAnswer as string} alt="imageOne" />
          )}
        </div>

        {/* Question Two */}
        {((siteImageOne !== '' && siteImageOne != null) ||
          (!isCoach &&
            questions[3].questionAnswer !== '' &&
            questions[3].questionAnswer !== undefined)) && (
          <div key={'Question 2'}>
            <Typography
              type="body"
              text={questions[3].question}
              color="primary"
            />
            {isCoach ? (
              <>
                <ImageInput
                  className="mb-4"
                  acceptedFormats={imageExtensions}
                  resolutionLimit={IMAGE_WIDTH}
                  label=""
                  nameProp="SitePhotoTwo"
                  icon="CameraIcon"
                  iconContainerColor="tertiary"
                  currentImageString={siteImageTwo}
                  overrideOnClick={() => setPhotoActionBarVisibleTwo(true)}
                />

                {isPhotoActionBarVisibleTwo && (
                  <Dialog visible position={DialogPosition.Bottom} stretch>
                    <PhotoPrompt
                      title="Collage photo"
                      resolutionLimit={IMAGE_WIDTH}
                      onClose={() => setPhotoActionBarVisibleTwo(false)}
                      onAction={(base64: string) => {
                        onOptionSelected('', 3);
                        setSiteImageTwo(base64);
                        setPhotoActionBarVisibleTwo(false);
                      }}
                      onDelete={() => {
                        setSiteImageTwo('');
                        onOptionSelected('', 3);
                        setPhotoActionBarVisibleTwo(false);
                      }}
                    />
                  </Dialog>
                )}
              </>
            ) : (
              <img src={questions[3].questionAnswer as string} alt="imageTwo" />
            )}
          </div>
        )}

        <Typography
          type="h2"
          text="Confirmation and signature"
          color="textDark"
          className="my-3"
        />
        <Divider dividerType="dashed" className="my-4" />

        {/* Question Three */}

        <CheckboxGroup
          id={questions[1].question}
          key={questions[1].question}
          title=""
          description={questions[1].question}
          checked={questions.some(
            (option) =>
              option.question === questions[1].question &&
              option.questionAnswer === 'true'
          )}
          value={questions[1].question}
          onChange={() =>
            onOptionSelected(
              questions[1].questionAnswer === 'true' ? 'false' : 'true',
              1
            )
          }
          className="mb-1"
          disabled={!isCoach}
        />

        {/* Question Four */}
        <div key={'Question'}>
          <Typography
            type="h4"
            text={`Give the phone to ${practitioner.user?.firstName} & ask them to sign below:`}
            color="textDark"
            className="my-3"
          />
          <Typography
            type="body"
            text="Practitioner signature:"
            color="primary"
          />
          {isCoach ? (
            <>
              <SignatureCanvas
                canvasProps={{
                  className:
                    'w-full bg-white h-60 rounded mb-4 mt-4 border border-textDark',
                }}
                ref={(ref) => {
                  signature = ref;
                }}
              />
              <div className="ml-3 flex w-full items-center justify-center">
                <XCircleIcon
                  className="text-errorMain h-11 w-11"
                  onClick={clearSignature}
                />
                <CheckCircleIcon
                  className="text-successMain h-11 w-11"
                  onClick={() => {
                    const signatureString = signature!
                      .getTrimmedCanvas()
                      .toDataURL('image/png');
                    saveSignature(signatureString);
                  }}
                />
              </div>
            </>
          ) : (
            <img src={questions[2].questionAnswer as string} alt="signature" />
          )}
        </div>

        <div className="mt-2 space-y-4">
          <Button
            type="filled"
            color="primary"
            className="mt-1 mb-2 w-full"
            onClick={() => {
              if (!isCoach) {
                setNotificationStep('');
              } else {
                saveSmartSpaceCheckData(questions, visitSection);
                onSubmit();
                history.push(ROUTES.COACH_FRANCHISE_AGREEMENT, {
                  practitioner,
                });
              }
            }}
            disabled={
              questions[0].questionAnswer === '' ||
              questions[1].questionAnswer === 'false' ||
              questions[2].questionAnswer === ''
            }
          >
            {renderIcon('DownloadIcon', 'mr-2 text-white w-5')}
            <Typography type="help" text="Save & continue" color="white" />
          </Button>
        </div>
      </div>
    </>
  );
};
